import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, Square, Plus, Trash2, ChevronDown, ChevronRight, Package, ClipboardList, X } from 'lucide-react';
import { API_URL } from '../config';

// Catégories disponibles
const CATEGORIES = [
  { value: 'materiel', label: 'Matériel' },
  { value: 'cuisine', label: 'Cuisine' },
  { value: 'decoration', label: 'Décoration' },
  { value: 'logistique', label: 'Logistique' },
  { value: 'documents', label: 'Documents' },
  { value: 'general', label: 'Général' },
];

// Couleurs par catégorie
const CATEGORIE_COLORS = {
  materiel: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-800 dark:text-amber-200', border: 'border-amber-300 dark:border-amber-700', bar: 'bg-amber-500' },
  cuisine: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-800 dark:text-emerald-200', border: 'border-emerald-300 dark:border-emerald-700', bar: 'bg-emerald-500' },
  decoration: { bg: 'bg-pink-100 dark:bg-pink-900/40', text: 'text-pink-800 dark:text-pink-200', border: 'border-pink-300 dark:border-pink-700', bar: 'bg-pink-500' },
  logistique: { bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-800 dark:text-cyan-200', border: 'border-cyan-300 dark:border-cyan-700', bar: 'bg-cyan-500' },
  documents: { bg: 'bg-violet-100 dark:bg-violet-900/40', text: 'text-violet-800 dark:text-violet-200', border: 'border-violet-300 dark:border-violet-700', bar: 'bg-violet-500' },
  general: { bg: 'bg-elevated-1', text: 'text-text-primary', border: 'border-border-strong', bar: 'bg-gray-500' },
};

const WhatsAppIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

const EventChecklist = ({ eventId, authUser, authToken, readOnly = false }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [newChecklistData, setNewChecklistData] = useState({ titre: '', categorie: 'general' });
  const [newItemData, setNewItemData] = useState({});
  const [addingItemFor, setAddingItemFor] = useState(null);

  const isAdmin = authUser && ['Patron', 'Super Manager'].includes(authUser.role_name);
  const canEdit = isAdmin && !readOnly;

  const loadChecklists = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/checklists`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error('Erreur chargement');
      const data = await res.json();
      setChecklists(data);
      // Expand all by default on first load
      if (Object.keys(expandedIds).length === 0 && data.length > 0) {
        const expanded = {};
        data.forEach(cl => { expanded[cl.id] = true; });
        setExpandedIds(expanded);
      }
    } catch (err) {
      console.error('Erreur chargement checklists:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, authToken]);

  useEffect(() => { loadChecklists(); }, [loadChecklists]);

  const toggleExpand = (clId) => {
    setExpandedIds(prev => ({ ...prev, [clId]: !prev[clId] }));
  };

  // Création d'une checklist
  const handleCreateChecklist = async () => {
    if (!newChecklistData.titre.trim()) {
      alert('Le titre est requis');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/checklists`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newChecklistData)
      });
      if (!res.ok) { const err = await res.json(); alert(err.error || 'Erreur création'); return; }
      setShowAddModal(false);
      setNewChecklistData({ titre: '', categorie: 'general' });
      await loadChecklists();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // Suppression d'une checklist
  const handleDeleteChecklist = async (clId) => {
    if (!window.confirm('Supprimer cette checklist et tous ses items ?')) return;
    try {
      await fetch(`${API_URL}/evenements/${eventId}/checklists/${clId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      await loadChecklists();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // Ajout d'un item
  const handleAddItem = async (clId) => {
    const itemData = newItemData[clId];
    if (!itemData || !itemData.label?.trim()) {
      alert('Le libellé est requis');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/checklists/${clId}/items`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: itemData.label, quantite: itemData.quantite || 1 })
      });
      if (!res.ok) { const err = await res.json(); alert(err.error || 'Erreur ajout'); return; }
      setNewItemData(prev => ({ ...prev, [clId]: { label: '', quantite: 1 } }));
      setAddingItemFor(null);
      await loadChecklists();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // Validation/dévalidation d'un item
  const handleToggleItem = async (clId, itemId, currentValue) => {
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/checklists/${clId}/items/${itemId}/validate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ est_valide: !currentValue })
      });
      if (!res.ok) { const err = await res.json(); alert(err.error || 'Erreur validation'); return; }
      await loadChecklists();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // Suppression d'un item
  const handleDeleteItem = async (clId, itemId) => {
    if (!window.confirm('Supprimer cet item ?')) return;
    try {
      await fetch(`${API_URL}/evenements/${eventId}/checklists/${clId}/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      await loadChecklists();
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // Calcul progression globale
  const allItems = checklists.flatMap(cl => cl.items || []);
  const totalItems = allItems.length;
  const validatedItems = allItems.filter(item => item.est_valide).length;
  const globalPct = totalItems > 0 ? Math.round((validatedItems / totalItems) * 100) : 0;

  const shareChecklistsWhatsApp = () => {
    if (checklists.length === 0) { alert('Aucune checklist à partager'); return; }
    let msg = `*Checklists de préparation*\n`;
    msg += `${validatedItems}/${totalItems} items validés (${globalPct}%)\n\n`;
    checklists.forEach(cl => {
      const items = cl.items || [];
      const clDone = items.filter(i => i.est_valide).length;
      msg += `📋 *${cl.titre}* (${clDone}/${items.length})\n`;
      items.forEach(item => {
        const check = item.est_valide ? '✅' : '⬜';
        const qty = item.quantite > 1 ? ` x${item.quantite}` : '';
        msg += `   ${check} ${item.label}${qty}\n`;
      });
      msg += '\n';
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return <div className="text-center py-8 text-text-muted">Chargement des checklists...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-text-primary">Checklists</h3>
        <div className="flex gap-2">
          {checklists.length > 0 && (
            <button
              onClick={shareChecklistsWhatsApp}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition"
            >
              <WhatsAppIcon /> WhatsApp
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
            >
              <Plus className="w-4 h-4" /> Checklist
            </button>
          )}
        </div>
      </div>

      {/* Barre de progression globale */}
      {totalItems > 0 && (
        <div className="bg-surface rounded-lg p-3 border">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-text-secondary flex items-center gap-1.5">
              <ClipboardList className="w-4 h-4" /> Préparation
            </span>
            <span className="font-bold text-text-primary">{validatedItems}/{totalItems} items validés ({globalPct}%)</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${globalPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${globalPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Liste des checklists */}
      {checklists.length === 0 ? (
        <div className="text-center py-8 bg-elevated-1 rounded-xl border-2 border-dashed border-border-strong">
          <Package className="w-12 h-12 text-text-muted mx-auto mb-2" />
          <p className="text-text-muted">Aucune checklist définie</p>
          {canEdit && <p className="text-sm text-text-muted mt-1">Cliquez sur "+ Checklist" pour en créer une</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {checklists.map(cl => {
            const colors = CATEGORIE_COLORS[cl.categorie] || CATEGORIE_COLORS.general;
            const items = cl.items || [];
            const clValidated = items.filter(i => i.est_valide).length;
            const clTotal = items.length;
            const clPct = clTotal > 0 ? Math.round((clValidated / clTotal) * 100) : 0;
            const isExpanded = expandedIds[cl.id];
            const itemInput = newItemData[cl.id] || { label: '', quantite: 1 };

            return (
              <div key={cl.id} className={`rounded-lg border ${colors.border} overflow-hidden`}>
                {/* Checklist header (accordion) */}
                <div
                  className={`flex items-center justify-between p-3 cursor-pointer ${colors.bg} hover:opacity-90 transition`}
                  onClick={() => toggleExpand(cl.id)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isExpanded
                      ? <ChevronDown className={`w-4 h-4 shrink-0 ${colors.text}`} />
                      : <ChevronRight className={`w-4 h-4 shrink-0 ${colors.text}`} />
                    }
                    <span className={`font-bold ${colors.text} truncate`}>{cl.titre}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
                      {CATEGORIES.find(c => c.value === cl.categorie)?.label || cl.categorie}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-medium ${colors.text}`}>{clValidated}/{clTotal}</span>
                    {canEdit && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteChecklist(cl.id); }}
                        className="p-1 text-red-500 hover:bg-red-50 dark:bg-red-950/40 rounded transition"
                        title="Supprimer la checklist"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Barre de progression par checklist */}
                {clTotal > 0 && (
                  <div className="px-3 pt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${clPct === 100 ? 'bg-green-500' : colors.bar}`}
                        style={{ width: `${clPct}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Items */}
                {isExpanded && (
                  <div className="p-3 bg-surface space-y-1">
                    {items.length === 0 && (
                      <p className="text-sm text-text-muted italic py-2">Aucun item dans cette checklist</p>
                    )}
                    {items.map(item => (
                      <div key={item.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-elevated-1 group">
                        <button
                          onClick={() => !readOnly && handleToggleItem(cl.id, item.id, item.est_valide)}
                          disabled={readOnly}
                          className={`shrink-0 ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                        >
                          {item.est_valide
                            ? <CheckSquare className="w-5 h-5 text-green-600 dark:text-green-400" />
                            : <Square className="w-5 h-5 text-text-muted" />
                          }
                        </button>
                        <span className={`flex-1 text-sm ${item.est_valide ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                          {item.label}
                        </span>
                        {item.quantite && item.quantite > 1 && (
                          <span className="text-xs px-1.5 py-0.5 bg-elevated-1 text-text-secondary rounded font-medium">
                            x{item.quantite}
                          </span>
                        )}
                        {item.est_valide && item.validator_name && (
                          <span className="text-xs text-green-600 dark:text-green-400 italic">
                            {item.validator_name}
                            {item.validated_at && ` - ${new Date(item.validated_at).toLocaleString('fr-FR')}`}
                          </span>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => handleDeleteItem(cl.id, item.id)}
                            className="p-1 text-red-400 hover:text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 transition"
                            title="Supprimer l'item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Ajout d'item inline */}
                    {canEdit && (
                      <div className="pt-2 border-t mt-2">
                        {addingItemFor === cl.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={itemInput.label}
                              onChange={e => setNewItemData(prev => ({ ...prev, [cl.id]: { ...itemInput, label: e.target.value } }))}
                              className="flex-1 px-2 py-1.5 border rounded-lg text-sm"
                              placeholder="Libellé de l'item..."
                              onKeyDown={e => { if (e.key === 'Enter') handleAddItem(cl.id); }}
                              autoFocus
                            />
                            <input
                              type="number"
                              value={itemInput.quantite}
                              onChange={e => setNewItemData(prev => ({ ...prev, [cl.id]: { ...itemInput, quantite: parseInt(e.target.value) || 1 } }))}
                              className="w-16 px-2 py-1.5 border rounded-lg text-sm text-center"
                              min="1"
                              placeholder="Qté"
                            />
                            <button
                              onClick={() => handleAddItem(cl.id)}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                            >
                              Ajouter
                            </button>
                            <button
                              onClick={() => setAddingItemFor(null)}
                              className="px-2 py-1.5 border rounded-lg text-sm hover:bg-elevated-1 transition"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setAddingItemFor(cl.id); setNewItemData(prev => ({ ...prev, [cl.id]: { label: '', quantite: 1 } })); }}
                            className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200 transition"
                          >
                            <Plus className="w-3.5 h-3.5" /> Item
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modale création de checklist */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h3 className="text-lg font-bold text-text-primary">Nouvelle checklist</h3>
              <button onClick={() => { setShowAddModal(false); setNewChecklistData({ titre: '', categorie: 'general' }); }} className="p-2 hover:bg-elevated-1 rounded-full transition">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Titre *</label>
                <input
                  type="text"
                  value={newChecklistData.titre}
                  onChange={e => setNewChecklistData({ ...newChecklistData, titre: e.target.value })}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg"
                  placeholder="Ex: Matériel cuisine"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Catégorie</label>
                <select
                  value={newChecklistData.categorie}
                  onChange={e => setNewChecklistData({ ...newChecklistData, categorie: e.target.value })}
                  className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border rounded-lg"
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button
                onClick={() => { setShowAddModal(false); setNewChecklistData({ titre: '', categorie: 'general' }); }}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border hover:bg-elevated-1 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateChecklist}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventChecklist;
